import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
  FaArrowLeft, 
  FaCheck, 
  FaLocationArrow, 
  FaClock, 
  FaCalendarAlt, 
  FaExclamationTriangle, 
  FaCalendarDay, 
  FaCalendarWeek,
  FaChair,
  FaCar,
  FaAngleDown,
  FaInfo
} from 'react-icons/fa';
import { supabase } from '../../../data/supabaseClient';
import { AuthContext } from '../../../Provider';
import { useToast } from '../../../hooks/ToastContext';
import Spinner from '../../../common/Spinner';
import GoogleMapsRoute from '../../../GoogleMapsRoute';
import { generateRoutePoints } from '../../../../utils/routeMatchingUtils';
import { CustomButton2, CustomButton8 } from '../../../Templates';
import '../../styles/GroupsPage.css';
import '../../../PublishPost.css';

const CreateGroupRide = () => {
  const { id } = useParams();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  const mapComponentRef = useRef();
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [isCarsLoading, setIsCarsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [group, setGroup] = useState(null);
  const [userMember, setUserMember] = useState(null);
  const [cars, setCars] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    selectedDate: new Date(),
    selectedTime: new Date(),
    seats: 1,
    selectedCar: '',
    address_from: {
      street: '',
      city: '',
      country: ''
    },
    address_to: {
      street: '',
      city: '',
      country: ''
    },
    static_map_url: null,
    distance_km: null,
    route_polyline: null,
    error: null
  });
  
  // Dropdown state
  const [showSeatsMenu, setShowSeatsMenu] = useState(false);
  const [showCarsMenu, setShowCarsMenu] = useState(false);
  
  // Load group and user data
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!user) {
          navigate('/login');
          return;
        }
        
        if (!id) {
          console.error('Group ID is undefined');
          addToast({
            type: 'error',
            title: t('errors.error'),
            description: t('errors.group_fetch_failed')
          });
          navigate('/groups');
          return;
        }
        
        setLoading(true);
        
        // Fetch group details
        const { data: groupData, error: groupError } = await supabase
          .from('groups')
          .select('*')
          .eq('id', id)
          .single();
        
        if (groupError) throw groupError;
        
        setGroup(groupData);
        
        // Check if user is a member of the group
        const { data: memberData, error: memberError } = await supabase
          .from('group_members')
          .select('*')
          .eq('group_id', id)
          .eq('user_id', user.id)
          .eq('status', 'active')
          .single();
        
        if (memberError) {
          console.error('Error checking membership:', memberError);
          // If not a member, redirect back to the group page
          addToast({
            type: 'error',
            title: t('errors.access_denied'),
            description: t('errors.not_group_member')
          });
          navigate(`/groups/${id}`);
          return;
        }
        
        setUserMember(memberData);
        
        // If group has a meeting point, pre-fill it as the starting location
        if (groupData.meeting_point) {
          setFormData(prev => ({
            ...prev,
            address_from: {
              street: groupData.meeting_point,
              city: groupData.location || '',
              country: ''
            }
          }));
        }
        
        // Fetch user's cars
        const { data: carsData, error: carsError } = await supabase
          .from('cars')
          .select('*')
          .eq('user_id', user.id);
        
        if (carsError) throw carsError;
        
        setCars(carsData || []);
        setIsCarsLoading(false);
        
      } catch (error) {
        console.error('Error fetching data:', error);
        addToast({
          type: 'error',
          title: t('errors.error'),
          description: t('errors.data_fetch_failed')
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, user, navigate, t, addToast]);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (showSeatsMenu && !e.target.closest('.seats-selector')) {
        setShowSeatsMenu(false);
      }
      if (showCarsMenu && !e.target.closest('.car-selector')) {
        setShowCarsMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, [showSeatsMenu, showCarsMenu]);
  
  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      error: null // Clear any previous error for this field
    }));
  };
  
  // Handle location selection from map component
  const handleLocationSelect = (field, value) => {
    console.log(`handleLocationSelect called with field: ${field}, value:`, value);
    
    if (field === 'distance_km') {
      setFormData(prev => ({
        ...prev,
        distance_km: value
      }));
    } else if (field === 'static_map_url') {
      setFormData(prev => ({
        ...prev,
        static_map_url: value
      }));
    } else if (field === 'route_polyline') {
      setFormData(prev => ({
        ...prev,
        route_polyline: value
      }));
      console.log('Route polyline saved to form state');
    }

    // Parse the Google Places result
    if (value && value.address_components) {
      const streetNumber = value.address_components.find(c => c.types.includes('street_number'))?.long_name || '';
      const route = value.address_components.find(c => c.types.includes('route'))?.long_name || '';
      const city = value.address_components.find(c => c.types.includes('locality'))?.long_name || 
                  value.address_components.find(c => c.types.includes('administrative_area_level_2'))?.long_name || '';
      const country = value.address_components.find(c => c.types.includes('country'))?.long_name || '';

      const street = streetNumber ? `${streetNumber} ${route}` : route || value.formatted_address;
      const addressField = field === 'address_from' ? 'address_from' : 'address_to';

      console.log(`Setting ${addressField} to:`, { 
        street: street || value.formatted_address, 
        city, 
        country 
      });

      setFormData(prev => ({
        ...prev,
        [addressField]: {
          street: street || value.formatted_address,
          city,
          country
        }
      }));
    } else if (field === 'address_from' || field === 'address_to') {
      // If it's a direct address string (not a Google Place object)
      console.log(`Setting ${field} with string:`, value);
      
      // Extract what might be city/country from the string if possible
      const parts = typeof value === 'string' ? value.split(',').map(p => p.trim()) : [];
      
      setFormData(prev => ({
        ...prev,
        [field]: {
          street: typeof value === 'string' ? value : '',
          city: parts.length > 1 ? parts[1] : '',
          country: parts.length > 2 ? parts[2] : ''
        }
      }));
    }
  };
  
  // Handle dropdown toggles
  const toggleSeatsMenu = (e) => {
    e.stopPropagation();
    setShowSeatsMenu(!showSeatsMenu);
    setShowCarsMenu(false);
  };
  
  const toggleCarsMenu = (e) => {
    e.stopPropagation();
    setShowCarsMenu(!showCarsMenu);
    setShowSeatsMenu(false);
  };
  
  // Handle seat selection
  const handleSeatSelect = (seatNumber) => {
    setFormData(prev => ({ ...prev, seats: seatNumber }));
    setShowSeatsMenu(false);
  };
  
  // Handle car selection
  const handleCarSelect = (car) => {
    setFormData(prev => ({ ...prev, selectedCar: car }));
    setShowCarsMenu(false);
  };
  
  // Format time string for database
  const formatTimeString = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };
  
  // Validate form before submission
  const validateForm = () => {
    // Check required fields
    if (!formData.title.trim()) {
      setFormData(prev => ({ 
        ...prev, 
        error: t('errors.title_required')
      }));
      return false;
    }
    
    if (!formData.selectedCar) {
      setFormData(prev => ({ 
        ...prev, 
        error: t('forms.car_required')
      }));
      return false;
    }
    
    // Check if both addresses are provided
    if (!formData.address_from.street || !formData.address_to.street) {
      setFormData(prev => ({ 
        ...prev, 
        error: t('forms.address_required')
      }));
      return false;
    }
    
    // Check if route has been calculated
    if (!formData.distance_km) {
      setFormData(prev => ({
        ...prev,
        error: t('forms.calculate_route')
      }));
      return false;
    }
    
    // Check if route polyline is captured
    if (!formData.route_polyline) {
      setFormData(prev => ({
        ...prev,
        error: t('forms.missing_route_data')
      }));
      return false;
    }
    
    return true;
  };
  
  // Create the group ride
  const handleCreateRide = async () => {
    if (!validateForm()) {
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: formData.error
      });
      return;
    }
    
    setIsCreating(true);
    
    try {
      // Create group ride
      const { data: rideData, error: rideError } = await supabase
        .from('group_rides')
        .insert({
          group_id: id,
          title: formData.title.trim(),
          description: formData.description.trim(),
          driver_id: user.id,
          travel_date: formData.selectedDate,
          travel_time: formatTimeString(formData.selectedTime),
          travel_type: 'one-time',
          recurrence_pattern: null,
          address_from_street: formData.address_from.street,
          address_from_city: formData.address_from.city,
          address_from_country: formData.address_from.country,
          address_to_street: formData.address_to.street,
          address_to_city: formData.address_to.city,
          address_to_country: formData.address_to.country,
          map_image_url: formData.static_map_url,
          route_polyline: formData.route_polyline,
          distance_km: formData.distance_km,
          max_seats: formData.seats,
          available_seats: formData.seats,
          status: 'active'
        })
        .select()
        .single();
      
      if (rideError) throw rideError;
      
      // Add driver as a participant
      const { error: participantError } = await supabase
        .from('group_ride_participants')
        .insert({
          ride_id: rideData.id,
          user_id: user.id,
          role: 'driver',
          status: 'confirmed'
        });
      
      if (participantError) throw participantError;
      
      // Generate route points for matching
      if (formData.route_polyline) {
        try {
          console.log('Generating route points for group ride ID:', rideData.id);
          await generateRoutePoints(formData.route_polyline, {
            postId: null,
            packageId: null,
            groupRideId: rideData.id,
            intervalKm: 5
          });
        } catch (routeError) {
          console.error('Error generating route points:', routeError);
          // Continue even if route points fail, we'll log but not stop the process
        }
      }
      
      // Success!
      addToast({
        type: 'success',
        title: t('success'),
        description: t('groups.ride_created_success')
      });
      
      // Navigate back to group
      navigate(`/groups/${id}`);
      
    } catch (error) {
      console.error('Error creating group ride:', error);
      addToast({
        type: 'error',
        title: t('errors.creation_failed'),
        description: t('errors.ride_creation_failed')
      });
      setIsCreating(false);
    }
  };
  
  // Navigate back to group page
  const handleBack = () => {
    navigate(`/groups/${id}`);
  };
  
  if (loading) {
    return (
      <>
        <div className="groups-page">
          <Spinner size="large" />
        </div>
      </>
    );
  }
  
  return (
    <>
      <div className="publish-post-container">
        <div className="back-button">
          <CustomButton8 onClick={handleBack}>
            <FaArrowLeft />
          </CustomButton8>
        </div>
        
        <h2 className="form-heading">{t('groups.create_ride_for_group')}</h2>
        <p className="group-context">{t('groups.for_group')}: <strong>{group?.name}</strong></p>
        
        {formData.error && (
          <div className="error-message">
            <FaExclamationTriangle />
            <span>{formData.error}</span>
          </div>
        )}
        
        <section className="form-section">
          <h3>
            <FaInfo style={{ marginRight: '8px' }} />
            {t('groups.ride_details')}
          </h3>
          <div className="form-group">
            <label htmlFor="title">{t('groups.ride_title')} *</label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder={t('groups.ride_title_placeholder')}
            />
          </div>
          <div className="form-group">
            <label htmlFor="description">{t('groups.ride_description')}</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder={t('groups.ride_description_placeholder')}
              rows={3}
            />
          </div>
        </section>
        
        <section className="form-section">
          <h3>
            <FaCalendarAlt style={{ marginRight: '8px' }} />
            {t('forms.leaving_at')}
          </h3>
          <div className="datetime-container">
            <div className="date-picker-wrapper">
              <FaCalendarAlt className="icon" />
              <DatePicker
                selected={formData.selectedDate}
                onChange={(date) => handleChange('selectedDate', date)}
                dateFormat="dd/MM/yyyy"
                className="date-picker-input"
                minDate={new Date()}
              />
            </div>
            <div className="time-picker-wrapper">
              <FaClock className="icon" />
              <DatePicker
                selected={formData.selectedTime}
                onChange={(time) => handleChange('selectedTime', time)}
                showTimeSelect
                showTimeSelectOnly
                timeFormat="HH:mm"
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="HH:mm"
                className="time-picker-input"
              />
            </div>
          </div>
          {/* One-time/daily ride switch removed for group rides */}
        </section>
        
        <section className="form-section">
          <h3>
            <FaCar style={{ marginRight: '8px' }} />
            {t('forms.free_seats')} *
          </h3>
          <div className="additional-options">
            <div className="seats-selector">
              <div className="dropdown-control">
                <button 
                  type="button"
                  className="dropdown-button"
                  onClick={toggleSeatsMenu}
                >
                  <span>{formData.seats}</span>
                  <FaAngleDown className="dropdown-arrow" />
                </button>
                
                {showSeatsMenu && (
                  <ul className="dropdown-list seats-list">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(seatNumber => (
                      <li 
                        key={seatNumber}
                        className={formData.seats === seatNumber ? 'active' : ''}
                        onClick={() => handleSeatSelect(seatNumber)}
                      >
                        {seatNumber}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="car-selector">
              <h4>
                <FaCar style={{ marginRight: '8px' }} />
                {t('forms.car')} *
              </h4>
              <div className="dropdown-control">
                <button 
                  type="button"
                  className="dropdown-button"
                  onClick={toggleCarsMenu}
                >
                  <span>{formData.selectedCar || t('forms.select_car')}</span>
                  <FaAngleDown className="dropdown-arrow" />
                </button>
                
                {showCarsMenu && (
                  <ul className="dropdown-list cars-list">
                    {cars.length > 0 ? (
                      cars.map(car => (
                        <li 
                          key={car.id}
                          className={formData.selectedCar === `${car.brand} ${car.model} - ${car.number}` ? 'active' : ''}
                          onClick={() => handleCarSelect(`${car.brand} ${car.model} - ${car.number}`)}
                        >
                          {car.brand} {car.model} - {car.number}
                        </li>
                      ))
                    ) : (
                      <li className="no-cars">
                        {t('profile.no_cars')}
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </section>
        
        <section className="form-section">
          <h3>
            <FaLocationArrow style={{ marginRight: '8px' }} />
            {t('forms.travel_route')}
          </h3>
          
          <div className="map-container2">
            <GoogleMapsRoute 
              ref={mapComponentRef}
              onLocationSelect={handleLocationSelect}
              initialFromAddress={formData.address_from.street}
            />
          </div>
          
          <div className="publish-button-container">
            <CustomButton2 
              label={isCreating ? t('app.creating') : t('groups.create_ride')} 
              onClick={handleCreateRide}
              disabled={isCreating}
              icon={isCreating ? <Spinner size="small" showText={false} /> : <FaCheck />}
            />
          </div>
        </section>
      </div>
    </>
  );
};

export default CreateGroupRide;
